import { signal, effect } from '@preact/signals';
import { secureStorage } from '../storage/secureStorage.js';

/**
 * Free-form notes per verse. Stored as a flat ref → text map in
 * localStorage. Saving an empty/whitespace-only note removes the entry.
 */

export type Note = {
  ref: string;
  text: string;
  updatedAt: number;
};

const STORAGE_KEY = '_nt1';
const MAX_ENTRIES = 1000;

type Map = Record<string, Note>;

function load(): Map {
  if (typeof localStorage === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export const notes = signal<Map>(load());

if (typeof window !== 'undefined') {
  effect(() => {
    void secureStorage.setItem(STORAGE_KEY, JSON.stringify(notes.value));
  });
}

export function getNote(ref: string): Note | null {
  return notes.value[ref] ?? null;
}

export function setNote(ref: string, text: string): void {
  const trimmed = text.trim();
  const map = { ...notes.peek() };
  if (trimmed.length === 0) {
    delete map[ref];
  } else {
    map[ref] = { ref, text: trimmed, updatedAt: Date.now() };
  }
  const entries = Object.entries(map);
  if (entries.length > MAX_ENTRIES) {
    entries.sort((a, b) => b[1].updatedAt - a[1].updatedAt);
    const trimmedMap: Map = {};
    for (const [k, v] of entries.slice(0, MAX_ENTRIES)) trimmedMap[k] = v;
    notes.value = trimmedMap;
  } else {
    notes.value = map;
  }
}

export function removeNote(ref: string): void {
  setNote(ref, '');
}

export function listNotes(): Note[] {
  return Object.values(notes.value).sort((a, b) => b.updatedAt - a.updatedAt);
}
